import { useEffect, useState } from 'react';
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Code,
  Divider,
  Group,
  List,
  Modal,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  IconCheck,
  IconCopy,
  IconEdit,
  IconFileCode,
  IconFileText,
  IconListCheck,
  IconSend,
  IconTruck,
} from '@tabler/icons-react';
import {
  buildDefaultRouteSegments,
  validateOrderCompleteness,
  type ExpeditorDocumentDto,
  type OrderDetail,
  type OrderRouteSegmentInput,
} from '@expeditor/shared';
import { unitLabel, ownershipShort, type TransportUnitForm } from '../../shared/ui/transport-units';
import { managerOrdersApi, ordersApi } from '../../shared/api/orders';
import { visualizePdf } from '../../shared/api/pdf-viz';
import { shipmentsApi } from '../../shared/api/shipments';
import {
  directionLabel,
  orderStatusColor,
  orderStatusLabel,
  routePointKindLabel,
  transportLabel,
} from '../../shared/ui/order-labels';
import { DeliveryLegsSection } from '../delivery-legs/DeliveryLegsSection';
import { RouteSegmentsEditor } from './RouteSegmentsEditor';
import { OrderQuotesSection } from '../quotes/OrderQuotesSection';
import { useAuthStore } from '../../shared/auth/auth.store';

/** Концы маршрута (точки погрузки/выгрузки) — для раскладки по плечам. */
function routeEnds(order: OrderDetail) {
  const pickup = order.routePoints.find((p) => p.kind === 'PICKUP');
  const dropoff = order.routePoints.find((p) => p.kind === 'DROPOFF');
  return {
    pickupName: pickup?.address ?? '',
    pickupCountry: pickup?.country ?? '',
    dropoffName: dropoff?.address ?? '',
    dropoffCountry: dropoff?.country ?? '',
  };
}

/** Метки сторон уведомления о прибытии (формат ФНС: СвУведСторон). */
const NOTIFY_PARTY_LABEL: Record<string, string> = {
  CONSIGNEE: 'грузополучатель',
  SHIPPER: 'грузоотправитель',
  CLIENT: 'клиент',
};

/**
 * Карточка поручения — полные данные с кнопками действий.
 *
 * В режиме client-cabinet используется ordersApi (ownership-проверка на бэке).
 * Для менеджера будет отдельная страница, использующая managerOrdersApi.
 */
export function OrderDetailPage(props: { source?: 'client' | 'manager' }) {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const isManager = props.source === 'manager';
  const canActForClients = useAuthStore((s) => s.user?.canActForClients ?? false);
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Результат «Проверки» полноты: null — не проверяли, [] — всё ок, иначе список проблем.
  const [check, setCheck] = useState<string[] | null>(null);

  // Редактор плеч маршрута (менеджер; доступен и после подачи поручения).
  const [segmentsOpen, setSegmentsOpen] = useState(false);
  const [segmentsDraft, setSegmentsDraft] = useState<OrderRouteSegmentInput[]>([]);

  const onCheck = () => {
    if (!order) return;
    setCheck(validateOrderCompleteness(order));
  };

  const openSegmentsEditor = () => {
    if (!order) return;
    setSegmentsDraft(
      order.routeSegments.length > 0
        ? order.routeSegments.map((s) => ({
            ...s,
            dateFrom: s.dateFrom ?? undefined,
            dateTo: s.dateTo ?? undefined,
          }))
        : buildDefaultRouteSegments(order.transportModes, routeEnds(order)),
    );
    setSegmentsOpen(true);
  };

  const onSaveSegments = async () => {
    if (!order) return;
    setBusy(true);
    try {
      const updated = await managerOrdersApi.updateRouteSegments(order.id, {
        routeDetailed: segmentsDraft.length > 0,
        routeSegments: segmentsDraft,
      });
      setOrder(updated);
      setSegmentsOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сохранить плечи маршрута');
    } finally {
      setBusy(false);
    }
  };

  const reload = async () => {
    if (!id) return;
    try {
      const data = isManager ? await managerOrdersApi.getOne(id) : await ordersApi.getOne(id);
      setOrder(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить');
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onSubmit = async () => {
    if (!order) return;
    setBusy(true);
    try {
      const updated = isManager
        ? await managerOrdersApi.submit(order.id)
        : await ordersApi.submit(order.id);
      setOrder(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось подать');
    } finally {
      setBusy(false);
    }
  };

  const onFnsXml = async () => {
    if (!order) return;
    setBusy(true);
    try {
      const { fileName, xml } = await ordersApi.fnsTitle1(order.id);
      const url = URL.createObjectURL(new Blob([xml], { type: 'application/xml' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileName}.xml`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сформировать XML');
    } finally {
      setBusy(false);
    }
  };

  const onEtrn = async () => {
    if (!order) return;
    setBusy(true);
    try {
      const { fileName, xml } = await ordersApi.etrnTitleGO(order.id);
      const url = URL.createObjectURL(new Blob([xml], { type: 'application/xml' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileName}.xml`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сформировать ЭТрН');
    } finally {
      setBusy(false);
    }
  };

  const onExpRasp = async () => {
    if (!order) return;
    setBusy(true);
    try {
      const { fileName, xml } = await ordersApi.expRasp(order.id);
      const url = URL.createObjectURL(new Blob([xml], { type: 'application/xml' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileName}.xml`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сформировать расписку');
    } finally {
      setBusy(false);
    }
  };

  const onPorExpExp = async () => {
    if (!order) return;
    setBusy(true);
    try {
      const { fileName, xml } = await ordersApi.porExpExp(order.id);
      const url = URL.createObjectURL(new Blob([xml], { type: 'application/xml' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileName}.xml`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сформировать титул экспедитора');
    } finally {
      setBusy(false);
    }
  };

  // PDF-визуализация: берём XML из существующего эндпоинта, рендерим в PDF.
  const onPdf = async (getter: () => Promise<{ fileName: string; xml: string }>) => {
    if (!order) return;
    setBusy(true);
    try {
      const { fileName, xml } = await getter();
      await visualizePdf(fileName, xml);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось получить PDF');
    } finally {
      setBusy(false);
    }
  };

  const onPrintable = async () => {
    if (!order) return;
    setBusy(true);
    try {
      const { fileName, html } = isManager
        ? await managerOrdersApi.printable(order.id)
        : await ordersApi.printable(order.id);
      const url = URL.createObjectURL(new Blob([html], { type: 'application/msword' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileName}.doc`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сформировать печатную форму');
    } finally {
      setBusy(false);
    }
  };

  const onClone = async () => {
    if (!order) return;
    setBusy(true);
    try {
      const copy = await ordersApi.clone(order.id);
      nav(`/cabinet/orders/${copy.id}/edit`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось скопировать');
    } finally {
      setBusy(false);
    }
  };

  /** Ручное создание перевозки-проекта от этого поручения (менеджер). */
  const onCreateProject = async () => {
    if (!order) return;
    setBusy(true);
    try {
      const s = await shipmentsApi.createProject(order.id);
      nav(`/manager/shipments/${s.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось создать перевозку-проект');
    } finally {
      setBusy(false);
    }
  };

  if (error) return <Text c="red">{error}</Text>;
  if (!order) return <Text c="dimmed">Загрузка…</Text>;

  return (
    <Stack>
      <Group justify="space-between">
        <Group>
          <Title order={2}>Поручение {order.number}</Title>
          <Badge size="lg" color={orderStatusColor(order.status)}>
            {orderStatusLabel(order.status)}
          </Badge>
        </Group>
        {!isManager && (
          <Group>
            <Button
              leftSection={<IconListCheck size={16} />}
              onClick={onCheck}
              variant="default"
            >
              Проверка
            </Button>
            {order.status === 'DRAFT' && (
              <>
                <Button
                  component={Link}
                  to={`/cabinet/orders/${order.id}/edit`}
                  leftSection={<IconEdit size={16} />}
                  variant="default"
                >
                  Редактировать
                </Button>
                <Button
                  leftSection={<IconSend size={16} />}
                  onClick={onSubmit}
                  loading={busy}
                >
                  Подать на котировку
                </Button>
              </>
            )}
            <Button
              leftSection={<IconCopy size={16} />}
              onClick={onClone}
              loading={busy}
              variant="default"
            >
              Создать копию
            </Button>
            <Button
              leftSection={<IconFileText size={16} />}
              onClick={onPrintable}
              loading={busy}
              variant="light"
            >
              Печатная форма (Word)
            </Button>
            <Button.Group>
              <Button
                leftSection={<IconFileCode size={16} />}
                onClick={onFnsXml}
                loading={busy}
                variant="light"
              >
                Поручение XML
              </Button>
              <Button variant="light" onClick={() => onPdf(() => ordersApi.fnsTitle1(order.id))}>
                PDF
              </Button>
            </Button.Group>
            <Button.Group>
              <Button
                leftSection={<IconFileCode size={16} />}
                onClick={onEtrn}
                loading={busy}
                variant="light"
              >
                ЭТрН XML
              </Button>
              <Button variant="light" onClick={() => onPdf(() => ordersApi.etrnTitleGO(order.id))}>
                PDF
              </Button>
            </Button.Group>
            <Button.Group>
              <Button
                leftSection={<IconFileCode size={16} />}
                onClick={onPorExpExp}
                loading={busy}
                variant="light"
              >
                Титул экспедитора XML
              </Button>
              <Button variant="light" onClick={() => onPdf(() => ordersApi.porExpExp(order.id))}>
                PDF
              </Button>
            </Button.Group>
            <Button.Group>
              <Button
                leftSection={<IconFileCode size={16} />}
                onClick={onExpRasp}
                loading={busy}
                variant="light"
              >
                Расписка XML
              </Button>
              <Button variant="light" onClick={() => onPdf(() => ordersApi.expRasp(order.id))}>
                PDF
              </Button>
            </Button.Group>
          </Group>
        )}
        {isManager && (
          <Group>
            {canActForClients && order.status === 'DRAFT' && (
              <>
                <Button
                  component={Link}
                  to={`/manager/orders/${order.id}/edit`}
                  leftSection={<IconEdit size={16} />}
                  variant="default"
                >
                  Редактировать
                </Button>
                <Button leftSection={<IconSend size={16} />} onClick={onSubmit} loading={busy}>
                  Подать на котировку
                </Button>
              </>
            )}
            {/* Перевозка-проект: черновик под планирование плеч/документов и
                прикидку себестоимости до согласования. Пока перевозки нет. */}
            {order.shipmentId ? (
              <Button
                component={Link}
                to={`/manager/shipments/${order.shipmentId}`}
                leftSection={<IconTruck size={16} />}
                variant="default"
              >
                Открыть перевозку{order.shipmentStatus === 'PROJECT' ? ' (проект)' : ''}
              </Button>
            ) : (
              <Button
                leftSection={<IconTruck size={16} />}
                onClick={onCreateProject}
                loading={busy}
                variant="default"
              >
                Создать перевозку (проект)
              </Button>
            )}
            {/* Бланк нужен экспедитору не меньше, чем клиенту (печать, вложение
                в переписку) — тот же документ, что видит клиент. */}
            <Button
              leftSection={<IconFileText size={16} />}
              onClick={onPrintable}
              loading={busy}
              variant="light"
            >
              Печатная форма (Word)
            </Button>
          </Group>
        )}
      </Group>

      {isManager && (
        <OrderDocumentsCard
          orderId={order.id}
          canAct={canActForClients}
          onError={setError}
        />
      )}

      {check !== null &&
        (check.length === 0 ? (
          <Alert
            color="green"
            icon={<IconCheck size={18} />}
            title="Поручение заполнено"
            withCloseButton
            onClose={() => setCheck(null)}
          >
            Все обязательные поля заполнены — можно подавать на котировку и формировать XML.
          </Alert>
        ) : (
          <Alert
            color="orange"
            icon={<IconListCheck size={18} />}
            title="Не заполнены обязательные поля"
            withCloseButton
            onClose={() => setCheck(null)}
          >
            <List size="sm">
              {check.map((p) => (
                <List.Item key={p}>{p}</List.Item>
              ))}
            </List>
          </Alert>
        ))}

      <Card withBorder>
        <Title order={4}>Тип перевозки</Title>
        <Group>
          <Text>
            Транспорт:{' '}
            {(order.transportModes?.length ? order.transportModes : [order.transportMode])
              .map(transportLabel)
              .join(', ')}
          </Text>
          <Text>Направление: {directionLabel(order.direction)}</Text>
          {order.shipmentType && <Text>Тип: {order.shipmentType}</Text>}
        </Group>
        <Text mt={4}>
          Договор ТЭО:{' '}
          {order.agreementNumber
            ? `№ ${order.agreementNumber} от ${order.agreementSignedDate?.slice(0, 10) ?? ''}`
            : '— не выбран'}
        </Text>
      </Card>

      <Card withBorder>
        <Title order={4}>Груз</Title>
        <Stack gap="xs">
          <Text>
            <strong>{order.cargoName}</strong>
            {order.cargoOriginCountry && ` · страна происхождения: ${order.cargoOriginCountry}`}
          </Text>
          {(order.cargoTnVed || order.cargoOkpd2 || order.cargoEtSng || order.cargoGng) && (
            <Group gap="md">
              {order.cargoTnVed && <Text size="sm">ТН ВЭД: {order.cargoTnVed}</Text>}
              {order.cargoOkpd2 && <Text size="sm">ОКПД 2: {order.cargoOkpd2}</Text>}
              {order.cargoEtSng && <Text size="sm">ЕТ СНГ: {order.cargoEtSng}</Text>}
              {order.cargoGng && <Text size="sm">ГНГ: {order.cargoGng}</Text>}
            </Group>
          )}
          {order.cargoMarking && <Text size="sm">Маркировка: {order.cargoMarking}</Text>}
          <Group gap="md">
            {order.cargoUnits !== null && <Text size="sm">Мест: {order.cargoUnits}</Text>}
            {order.cargoPackaging && <Text size="sm">Упаковка: {order.cargoPackaging}</Text>}
          </Group>
          <Group gap="md">
            <Text>Вес брутто (с упаковкой): {order.cargoWeightKg} кг</Text>
            {order.cargoWeightNetKg !== null && (
              <Text>Нетто (без упаковки): {order.cargoWeightNetKg} кг</Text>
            )}
            {order.cargoTareWeightKg !== null && (
              <Text>Вес упаковки: {order.cargoTareWeightKg} кг</Text>
            )}
            {order.cargoVolumeM3 !== null && <Text>Объём: {order.cargoVolumeM3} м³</Text>}
          </Group>
          {(order.packageLengthM !== null ||
            order.packageWidthM !== null ||
            order.packageHeightM !== null) && (
            <Text size="sm">
              Размер упаковки: {order.packageLengthM ?? '—'} × {order.packageWidthM ?? '—'} ×{' '}
              {order.packageHeightM ?? '—'} м
            </Text>
          )}
          {order.cargoValue !== null && (
            <Text>
              Стоимость: {order.cargoValue} {order.cargoValueCurrency}
              {order.cargoVatRate !== null &&
                (order.cargoVatRate < 0 ? ' (Без НДС)' : ` (НДС ${order.cargoVatRate}%)`)}
            </Text>
          )}
          {order.deliveryTerms && <Text size="sm">Условия поставки: {order.deliveryTerms}</Text>}
          {(() => {
            const units: TransportUnitForm[] = (
              order.transportUnits && order.transportUnits.length > 0
                ? order.transportUnits.map((u) => ({
                    id: u.id ?? '',
                    kind: (u.kind ?? 'CONTAINER') as TransportUnitForm['kind'],
                    type: u.type,
                    number: u.number ?? '',
                    ownership: (u.ownership ?? '') as TransportUnitForm['ownership'],
                  }))
                : ((order.containers as Array<{ type: string; number?: string }>) ?? []).map(
                    (c, i) => ({
                      id: String(i),
                      kind: 'CONTAINER' as const,
                      type: c.type,
                      number: c.number ?? '',
                      ownership: '' as const,
                    }),
                  )
            );
            if (units.length === 0) return null;
            // Сводка по типам: «20' × 32 (5 с номерами)». Контейнеры и вагоны
            // могут быть в одном поручении — считаем по «вид + тип».
            const byType: Record<string, { total: number; withN: number }> = {};
            for (const u of units) {
              const key = u.kind === 'WAGON' ? `${u.type} (вагон)` : u.type;
              byType[key] = byType[key] || { total: 0, withN: 0 };
              byType[key].total++;
              if (u.number.trim()) byType[key].withN++;
            }
            const hasWagons = units.some((u) => u.kind === 'WAGON');
            const hasContainers = units.some((u) => u.kind === 'CONTAINER');
            const unitsTitle =
              hasWagons && hasContainers
                ? 'Единицы транспорта'
                : hasWagons
                  ? 'Вагоны'
                  : 'Контейнеры';
            // Сводка по принадлежности (порядок появления значений).
            const ownCounts: Record<string, number> = {};
            for (const u of units) if (u.ownership) ownCounts[u.ownership] = (ownCounts[u.ownership] ?? 0) + 1;
            const ownSummary = Object.entries(ownCounts)
              .map(([k, n]) => `${ownershipShort(k as TransportUnitForm['ownership'])}: ${n}`)
              .join(', ');
            const idToLabel = new Map(units.map((u, i) => [u.id, unitLabel(units, i)]));
            return (
              <Stack gap={2}>
                <Text size="sm">
                  {unitsTitle}:{' '}
                  {Object.entries(byType)
                    .map(
                      ([t, s]) =>
                        `${t} × ${s.total}${s.withN ? ` (${s.withN} с номерами)` : ''}`,
                    )
                    .join('; ')}
                </Text>
                {ownSummary && (
                  <Text size="sm" c="dimmed">
                    Принадлежность — {ownSummary}
                  </Text>
                )}
                {!order.sameCargoForAll && order.cargoGroups && order.cargoGroups.length > 0 && (
                  <Stack gap={2}>
                    <Text size="sm" fw={500}>
                      Груз по {hasWagons && !hasContainers ? 'вагонам' : 'единицам'}:
                    </Text>
                    {order.cargoGroups.map((g, i) => {
                      const keys = (g.unitIds && g.unitIds.length > 0 ? g.unitIds : g.containers) ?? [];
                      const labels = keys.map((k) => idToLabel.get(k) ?? k);
                      return (
                        <Text key={i} size="sm" c="dimmed">
                          • {g.cargoName || '—'}
                          {g.cargoWeightKg != null ? `, ${g.cargoWeightKg} кг` : ''}
                          {labels.length ? ` (${labels.join(', ')})` : ''}
                        </Text>
                      );
                    })}
                  </Stack>
                )}
              </Stack>
            );
          })()}
          <Group gap="xs">
            {order.cargoIsDangerous && (
              <Badge color="red">Опасный · ДОПОГ {order.cargoAdrClass}</Badge>
            )}
            {order.expeditorTakesPossession && (
              <Badge color="grape" variant="light">
                Приём во владение экспедитором
              </Badge>
            )}
            {order.notifyParties.length > 0 && (
              <Badge color="blue" variant="light">
                Уведомить: {order.notifyParties.map((p) => NOTIFY_PARTY_LABEL[p] ?? p).join(', ')}
              </Badge>
            )}
          </Group>
        </Stack>
      </Card>

      <Card withBorder>
        <Group justify="space-between">
          <Title order={4}>Маршрут</Title>
          {/* Плечи маршрута планируются на поручении, пока нет перевозки; после
              создания перевозки плечи ведутся в ней. */}
          {isManager && canActForClients && !order.shipmentId &&
            order.status !== 'CANCELLED' && order.status !== 'COMPLETED' && (
              <Button size="xs" variant="default" onClick={openSegmentsEditor}>
                {order.routeSegments.length > 0 ? 'Изменить плечи' : 'Уточнить маршрут по плечам'}
              </Button>
            )}
        </Group>
        <Stack mt="sm">
          {order.routePoints.map((p, idx) => (
            <Group key={p.id} align="flex-start">
              <Badge color={p.kind === 'PICKUP' ? 'blue' : 'orange'}>
                {p.kind === 'PICKUP' ? 'Откуда' : 'Куда'}
              </Badge>
              <Stack gap={2}>
                <Text fw={500}>{p.country ? `${p.country}, ${p.address}` : p.address}</Text>
                {p.counterpartyName && <Text size="sm">{p.counterpartyName}</Text>}
                {(p.counterpartyInn || p.counterpartyKpp || p.counterpartyOgrn) && (
                  <Text size="xs" c="dimmed">
                    {[
                      p.counterpartyInn && `ИНН ${p.counterpartyInn}`,
                      p.counterpartyKpp && `КПП ${p.counterpartyKpp}`,
                      p.counterpartyOgrn && `ОГРН ${p.counterpartyOgrn}`,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </Text>
                )}
                {(p.contactName || p.contactPhone) && (
                  <Text size="sm" c="dimmed">
                    {p.contactName} {p.contactPhone}
                  </Text>
                )}
                {p.date && (
                  <Text size="sm" c="dimmed">
                    Дата: {p.date.slice(0, 10)}
                  </Text>
                )}
              </Stack>
              {idx < order.routePoints.length - 1 && <Divider />}
            </Group>
          ))}
        </Stack>

        {!order.shipmentId && order.routeSegments.length > 0 && (
          <>
            <Divider my="sm" />
            <Text fw={500} size="sm" mb="xs">
              Плечи маршрута
            </Text>
            <Stack gap="xs">
              {order.routeSegments.map((s) => (
                <Group key={s.id} align="flex-start" wrap="nowrap">
                  <Badge variant="light">{s.sequence}</Badge>
                  <Stack gap={2}>
                    <Text size="sm">
                      {s.fromName || routePointKindLabel(s.fromKind)} →{' '}
                      {s.toName || routePointKindLabel(s.toKind)}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {[
                        transportLabel(s.transportMode),
                        s.carrierName,
                        s.dateFrom && `с ${s.dateFrom.slice(0, 10)}`,
                        s.dateTo && `по ${s.dateTo.slice(0, 10)}`,
                        s.documentNumbers.length > 0 &&
                          `документы: ${s.documentNumbers.join(', ')}`,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </Text>
                  </Stack>
                </Group>
              ))}
            </Stack>
          </>
        )}
      </Card>

      {/* Пока нет перевозки — старый блок документов на плечи поручения (легаси).
          После создания перевозки плечи и документы ведутся в ней. */}
      {isManager && !order.shipmentId && order.routeSegments.length > 0 && (
        <DeliveryLegsSection
          source={{ kind: 'order', orderId: order.id, segments: order.routeSegments }}
        />
      )}

      {/* Перевозка создана — операционная часть (плечи, исполнители, документы,
          смета) ведётся в ней. */}
      {isManager && order.shipmentId && (
        <Card withBorder>
          <Group justify="space-between">
            <div>
              <Title order={4}>Перевозка</Title>
              <Text size="sm" c="dimmed">
                Плечи, исполнители, документы и смета — в перевозке
                {order.shipmentStatus === 'PROJECT' ? ' (проект)' : ''}.
              </Text>
            </div>
            <Button
              component={Link}
              to={`/manager/shipments/${order.shipmentId}`}
              leftSection={<IconTruck size={16} />}
            >
              Открыть перевозку
            </Button>
          </Group>
        </Card>
      )}

      {order.extraServices.length > 0 && (
        <Card withBorder>
          <Title order={4}>Доп. услуги</Title>
          <Group mt="sm">
            {order.extraServices.map((s) => (
              <Badge key={s.id}>{s.name ?? s.code}</Badge>
            ))}
          </Group>
        </Card>
      )}

      {order.attachments.length > 0 && (
        <Card withBorder>
          <Title order={4}>Документы</Title>
          <Stack mt="sm" gap="xs">
            {order.attachments.map((a) => (
              <Anchor
                key={a.id}
                href={ordersApi.downloadAttachmentUrl(a.id)}
                target="_blank"
              >
                📎 {a.fileName} ({Math.round(a.sizeBytes / 1024)} KB)
              </Anchor>
            ))}
          </Stack>
        </Card>
      )}

      {(order.specialMarks || order.tempMinC !== null || order.tempMaxC !== null) && (
        <Card withBorder>
          <Title order={4}>Особые отметки</Title>
          <Stack gap="xs" mt="sm">
            {(order.tempMinC !== null || order.tempMaxC !== null) && (
              <Text size="sm">
                Климат: {order.tempMinC !== null ? `не ниже ${order.tempMinC} °C` : ''}
                {order.tempMinC !== null && order.tempMaxC !== null ? ', ' : ''}
                {order.tempMaxC !== null ? `не выше ${order.tempMaxC} °C` : ''}
              </Text>
            )}
            {order.specialMarks && (
              <Code block>{order.specialMarks}</Code>
            )}
          </Stack>
        </Card>
      )}

      {order.comment && (
        <Card withBorder>
          <Title order={4}>Комментарий клиента</Title>
          <Code block mt="sm">
            {order.comment}
          </Code>
        </Card>
      )}

      <OrderQuotesSection
        orderId={order.id}
        viewerRole={isManager ? 'manager' : 'client'}
        orderStatus={order.status}
        orderServices={order.extraServices.map((s) => ({ code: s.code, name: s.name }))}
        onChange={reload}
      />

      <Modal
        opened={segmentsOpen}
        onClose={() => setSegmentsOpen(false)}
        title="Маршрут по плечам"
        size="xl"
      >
        <Stack>
          <RouteSegmentsEditor
            value={segmentsDraft}
            onChange={setSegmentsDraft}
            modes={order.transportModes}
            ends={routeEnds(order)}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setSegmentsOpen(false)}>
              Отмена
            </Button>
            <Button onClick={onSaveSegments} loading={busy}>
              Сохранить
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

const DOC_TYPE_LABEL: Record<string, string> = {
  POREXPKLT: 'Поручение (титул клиента)',
  POREXPEXP: 'Поручение (титул экспедитора)',
  POREXPOTZ: 'Отзыв поручения',
  EXPRASP: 'Экспедиторская расписка',
  SKLADRASP: 'Складская расписка',
  ETRN_GO: 'ЭТрН',
  ZAKZVGO: 'ЭЗЗ (заказ-заявка)',
};

/**
 * Экспедиторские документы заказа (Фаза 2, сценарий клиент→наш экспедитор):
 * «Принять поручение» (титул экспедитора POREXPEXP) и «Выпустить расписку»
 * (EXPRASP), список сохранённых документов со скачиванием XML.
 */
function OrderDocumentsCard({
  orderId,
  canAct,
  onError,
}: {
  orderId: string;
  canAct: boolean;
  onError: (m: string) => void;
}) {
  const [docs, setDocs] = useState<ExpeditorDocumentDto[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    managerOrdersApi.documents(orderId).then(setDocs).catch(() => {});
  }, [orderId]);

  const download = async (docId: string) => {
    try {
      const res = await managerOrdersApi.documentXml(orderId, docId);
      const url = URL.createObjectURL(new Blob([res.xml], { type: 'application/xml' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${res.fileName}.xml`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Не удалось скачать документ');
    }
  };
  const viewPdf = async (docId: string) => {
    try {
      const res = await managerOrdersApi.documentXml(orderId, docId);
      await visualizePdf(res.fileName, res.xml);
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Не удалось получить PDF');
    }
  };
  const accept = async () => {
    setBusy(true);
    try {
      setDocs(await managerOrdersApi.acceptPoruchenie(orderId));
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Не удалось принять поручение');
    } finally {
      setBusy(false);
    }
  };
  const issue = async () => {
    setBusy(true);
    try {
      setDocs(await managerOrdersApi.issueRaspiska(orderId));
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Не удалось выпустить расписку');
    } finally {
      setBusy(false);
    }
  };
  const revoke = async () => {
    setBusy(true);
    try {
      setDocs(await managerOrdersApi.revokePoruchenie(orderId));
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Не удалось отозвать поручение');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card withBorder radius="md" p="md" mt="md">
      <Group justify="space-between" mb="xs">
        <Text fw={600}>Экспедиторские документы</Text>
        {canAct && (
          <Group gap="xs">
            <Button size="xs" variant="light" onClick={accept} loading={busy}>
              Принять поручение
            </Button>
            <Button size="xs" variant="light" color="teal" onClick={issue} loading={busy}>
              Выпустить расписку
            </Button>
            <Button size="xs" variant="light" color="red" onClick={revoke} loading={busy}>
              Отозвать поручение
            </Button>
          </Group>
        )}
      </Group>
      {docs.length === 0 ? (
        <Text size="sm" c="dimmed">
          Документов пока нет.
        </Text>
      ) : (
        <Stack gap={4}>
          {docs.map((doc) => (
            <Group key={doc.id} justify="space-between" wrap="nowrap">
              <Text size="sm">
                <Badge
                  size="xs"
                  color={doc.direction === 'INCOMING' ? 'teal' : 'blue'}
                  mr={6}
                >
                  {doc.direction === 'INCOMING' ? 'входящий' : 'исходящий'}
                </Badge>
                {DOC_TYPE_LABEL[doc.type] ?? doc.type}
                {doc.number ? ` № ${doc.number}` : ''} ·{' '}
                {new Date(doc.createdAt).toLocaleDateString('ru-RU')}
              </Text>
              <Group gap={4} wrap="nowrap">
                <Button size="compact-xs" variant="subtle" onClick={() => viewPdf(doc.id)}>
                  PDF
                </Button>
                <Button size="compact-xs" variant="subtle" onClick={() => download(doc.id)}>
                  XML
                </Button>
              </Group>
            </Group>
          ))}
        </Stack>
      )}
    </Card>
  );
}
